import React, { useState } from 'react';
import { packages, postData } from '../components/misc';
import { trackEvent } from '../utils/bridge.js';

const bulkEndpoint = '/api/sirri_api/bulk_change_men_package';
const exportEndpoint = '/api/sirri_api/mens_utils_export';

export default function BulkPackageChange() {
  const [fromPackage, setFromPackage] = useState('');
  const [toPackage, setToPackage] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const resetMessages = () => {
    setError('');
    setResult(null);
  };

  const validateSelection = () => {
    if (!fromPackage || !toPackage) {
      setError('Select both from and to packages.');
      return false;
    }
    if (fromPackage === toPackage) {
      setError('From and to packages must be different.');
      return false;
    }
    return true;
  };

  const handlePreview = async () => {
    trackEvent('admin_bulk_package_preview_pressed')
    resetMessages();
    if (!validateSelection()) {
      return;
    }
    setLoadingPreview(true);
    try {
      const data = await postData(bulkEndpoint, {
        from_package: fromPackage,
        to_package: toPackage,
        dry_run: true
      });
      if (data?.responsecode !== 200) {
        setError(data?.message || 'Preview failed.');
      } else {
        trackEvent('admin_bulk_package_preview_success')
        setPreview(data);
      }
    } catch (err) {
      console.error('Failed to preview bulk package change', err);
      setError('Preview failed. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRun = async () => {
    trackEvent('admin_bulk_package_run_pressed')
    resetMessages();
    if (!validateSelection()) {
      return;
    }
    if (!confirmed) {
      setError('Please confirm before running the bulk change.');
      return;
    }
    setLoadingRun(true);
    try {
      const data = await postData(bulkEndpoint, {
        from_package: fromPackage,
        to_package: toPackage,
        dry_run: false
      });
      if (data?.responsecode !== 200) {
        setError(data?.message || 'Run failed.');
      } else {
        trackEvent('admin_bulk_package_run_success')
        setResult(data);
      }
    } catch (err) {
      console.error('Failed to run bulk package change', err);
      setError('Run failed. Please try again.');
    } finally {
      setLoadingRun(false);
    }
  };

  const handleExport = async () => {
    trackEvent('admin_bulk_package_export_pressed')
    setExportError('');
    setExporting(true);
    try {
      const response = await fetch(exportEndpoint);
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'mens_utils_export.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export men CSV', err);
      setExportError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const previewCount = preview?.updated_profiles?.length ?? 0;
  const resultCount = result?.updated_count ?? 0;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-gray-900 text-white rounded shadow p-4">
        <h1 className="text-xl font-semibold mb-2">Mens utils</h1>
        <p className="text-sm text-gray-300">Utilities for managing men profiles.</p>
      </div>

      <div className="bg-gray-900 text-white rounded shadow p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Bulk Package Change</h2>
          <p className="text-sm text-gray-300">
            Select a package to move from and a package to move to. Use Preview/Count to see how
            many profiles will be updated before running the change.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex flex-col text-sm">
            From package
            <select
              className="mt-1 text-black rounded p-2"
              value={fromPackage}
              onChange={(e) => setFromPackage(e.target.value)}
            >
              <option value="">Select package</option>
              {packages.map((pkg) => (
                <option key={`from-${pkg}`} value={pkg}>
                  {pkg}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm">
            To package
            <select
              className="mt-1 text-black rounded p-2"
              value={toPackage}
              onChange={(e) => setToPackage(e.target.value)}
            >
              <option value="">Select package</option>
              {packages.map((pkg) => (
                <option key={`to-${pkg}`} value={pkg}>
                  {pkg}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? (
          <div className="bg-red-900 text-red-200 p-2 rounded">{error}</div>
        ) : null}

        {preview ? (
          <div className="bg-gray-800 text-sm rounded p-3">
            <p>Matched profiles: {preview.matched_count}</p>
            <p>Profiles to update: {previewCount}</p>
          </div>
        ) : null}

        {result ? (
          <div className="bg-green-900 text-green-200 text-sm rounded p-3">
            <p>Matched profiles: {result.matched_count}</p>
            <p>Updated profiles: {resultCount}</p>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            className="bg-blue-700 px-4 py-2 rounded"
            onClick={handlePreview}
            disabled={loadingPreview}
          >
            {loadingPreview ? 'Previewing...' : 'Preview/Count'}
          </button>
          <button
            className="bg-red-700 px-4 py-2 rounded"
            onClick={handleRun}
            disabled={loadingRun}
          >
            {loadingRun ? 'Running...' : 'Run'}
          </button>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          I understand this will update live profiles.
        </label>
      </div>

      <div className="bg-gray-900 text-white rounded shadow p-4 space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Export men CSV</h2>
          <p className="text-sm text-gray-300">
            Download a CSV with fullname1, country1, city1, package1, and occupation1 (or
            currentoccupation1 when occupation1 is empty).
          </p>
        </div>
        {exportError ? (
          <div className="bg-red-900 text-red-200 p-2 rounded">{exportError}</div>
        ) : null}
        <button
          className="bg-green-700 px-4 py-2 rounded"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
    </div>
  );
}
